import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createVehicleInputSchema, type Vehicle } from 'fuel-benchmark-contracts';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { orpc } from '../../apis/orpc-query';
import { Button } from '../../components/ui/Button';
import type { SelectOption } from '../../components/ui/Select';
import { Sheet } from '../../components/ui/Sheet';
import { serverMessage, submitErrorKey } from '../../lib/form-helpers';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { EMPTY_VEHICLE_FORM_VALUES, toUpdatePayload, toVehicleFormValues, type VehicleFormValues } from './vehicle-display';
import { VehicleFormFields } from './VehicleFormFields';

export interface VehicleSheetProps {
  isOpen: boolean;
  onClose: () => void;
  segmentOptions: readonly SelectOption[];
  /** Absent for a create; present for an edit, which locks the code and PATCHes instead of POSTing. */
  vehicle?: Vehicle;
  onSaved?: (vehicleId: string) => void;
}

/**
 * Create/edit vehicle side panel. Both modes validate against the contract's create schema, so the
 * inline rules the API enforces are the exact rules the form shows.
 */
export const VehicleSheet = ({ isOpen, onClose, segmentOptions, vehicle, onSaved }: VehicleSheetProps) => {
  const { t } = useI18nTranslation();
  const queryClient = useQueryClient();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const isEdit = vehicle !== undefined;

  const form = useForm<VehicleFormValues>({
    resolver: zodResolver(createVehicleInputSchema),
    defaultValues: vehicle ? toVehicleFormValues(vehicle) : EMPTY_VEHICLE_FORM_VALUES,
  });

  // The sheet stays mounted between openings, so each opening re-seeds the fields from the current row.
  useEffect(() => {
    if (isOpen) {
      setSubmitError(null);
      form.reset(vehicle ? toVehicleFormValues(vehicle) : EMPTY_VEHICLE_FORM_VALUES);
    }
  }, [isOpen, vehicle, form]);

  const invalidate = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: orpc.vehicles.list.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.vehicles.get.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.vehicles.attributeOptions.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.benchmarks.dashboardStats.key() }),
    ]);
  };

  const createVehicle = useMutation(orpc.vehicles.create.mutationOptions());
  const updateVehicle = useMutation(orpc.vehicles.update.mutationOptions());

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);

    try {
      const saved = isEdit ? await updateVehicle.mutateAsync(toUpdatePayload(vehicle.id, values)) : await createVehicle.mutateAsync(values);

      await invalidate();
      onSaved?.(saved.id);
      onClose();
    } catch (error) {
      setSubmitError(serverMessage(error) ?? t(submitErrorKey(error)));
    }
  });

  const isSubmitting = form.formState.isSubmitting;

  return (
    <Sheet
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? t('vehicles.form.editTitle') : t('vehicles.form.createTitle')}
      description={isEdit ? t('vehicles.form.editSubtitle') : t('vehicles.form.createSubtitle')}
      footer={
        <>
          <Button
            variant="outline"
            onClick={onClose}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            type="submit"
            form="vehicle-form"
            isLoading={isSubmitting}
          >
            {isSubmitting ? t('actions.saving') : t('actions.save')}
          </Button>
        </>
      }
    >
      <form
        id="vehicle-form"
        noValidate
        onSubmit={onSubmit}
        className="space-y-6"
      >
        <VehicleFormFields
          form={form}
          segmentOptions={segmentOptions}
          isCodeLocked={isEdit}
        />

        {submitError ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {submitError}
          </p>
        ) : null}
      </form>
    </Sheet>
  );
};
